import { useState } from "react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mdendbvr";

const initialForm = { name: "", email: "", shop: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(e.target),
      });

      if (response.ok) {
        setStatus("success");
        setForm(initialForm);
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="bg-mist/60 py-20 sm:py-28">
      <div className="mx-auto max-w-content px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-light px-3.5 py-1.5 text-[13px] font-medium text-accent-dark">
              <Mail size={15} strokeWidth={2.25} />
              Demande de démo
            </span>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Voyons ensemble ce qu'Hygio peut simplifier chez vous
            </h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-ink-soft">
              Laissez-nous vos coordonnées : nous revenons vers vous
              rapidement pour organiser une démonstration adaptée à votre
              commerce.
            </p>

            <dl className="mt-10 space-y-5">
              <div>
                <dt className="text-sm font-medium text-ink-soft">
                  Réponse
                </dt>
                <dd className="text-[15px] text-ink">Sous 24 à 48h ouvrées</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-ink-soft">
                  Démonstration
                </dt>
                <dd className="text-[15px] text-ink">
                  En visio ou directement dans votre commerce
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-ink/[0.06] bg-white p-6 shadow-soft sm:p-8">
            {status === "success" ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-ink">
                  Merci, votre demande est bien envoyée
                </h3>
                <p className="mt-2 max-w-sm text-[15px] text-ink-soft">
                  Notre équipe revient vers vous très prochainement pour
                  organiser votre démo.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-1.5 block text-sm font-medium text-ink"
                    >
                      Nom
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jean Dupont"
                      className="w-full rounded-lg border border-ink/10 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-soft/60 outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/15"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1.5 block text-sm font-medium text-ink"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jean@moncommerce.fr"
                      className="w-full rounded-lg border border-ink/10 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-soft/60 outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/15"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="shop"
                    className="mb-1.5 block text-sm font-medium text-ink"
                  >
                    Nom du commerce
                  </label>
                  <input
                    id="shop"
                    name="shop"
                    type="text"
                    required
                    value={form.shop}
                    onChange={handleChange}
                    placeholder="Boulangerie du Marché"
                    className="w-full rounded-lg border border-ink/10 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-soft/60 outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/15"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-1.5 block text-sm font-medium text-ink"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Parlez-nous de votre commerce et de vos besoins..."
                    className="w-full resize-none rounded-lg border border-ink/10 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-soft/60 outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/15"
                  />
                </div>

                {/* Champ anti-spam (honeypot) — laissé vide et masqué */}
                <input
                  type="text"
                  name="_gotcha"
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                {status === "error" && (
                  <p className="text-sm text-red-600">
                    Une erreur est survenue. Vérifiez votre connexion ou
                    réessayez dans quelques instants.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 text-[15px] font-medium text-white transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" && (
                    <Loader2 size={17} className="animate-spin" />
                  )}
                  {status === "loading"
                    ? "Envoi en cours..."
                    : "Demander une démo"}
                </button>

                <p className="text-center text-xs text-ink-soft">
                  En envoyant ce formulaire, vous acceptez d'être recontacté
                  par l'équipe Hygio au sujet de votre demande.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
